'use client' // <- Only needed for Next.js App Router; remove if not using Next.js

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Icons
import {
  HomeIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  InboxIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
} from '@heroicons/react/20/solid';

import {
  UserIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  SparklesIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  ChatBubbleBottomCenterTextIcon,
  UsersIcon,
} from '@heroicons/react/16/solid';

// Components & Context
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_PROFILE_IMAGE } from '../services/api';
import { useApiService } from '../services/useApiService';
import useNotificationService from '../hooks/useNotificationService';
import NaibLogo from './NaibLogo';
import InitialsAvatar from './InitialsAvatar';
import { NotificationIcon } from './common/NotificationIcon';

// Catalyst Components
import { Avatar } from './catalyst/avatar';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownDivider,
  DropdownLabel,
  DropdownMenu,
} from './catalyst/dropdown';
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from './catalyst/navbar';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarSection,
  SidebarDivider,
  SidebarSpacer,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
} from './catalyst/sidebar';
import { SidebarLayout } from './catalyst/sidebar-layout';
import { api } from '../services/api';
import { CUSTOMER_INFO_UPDATED_EVENT } from '../constants/customerEvents';
import {
  isBillingInfoComplete,
  isAdministratorRole,
  normalizeCustomerPayload,
} from '../utils/customerBilling';
import FirstRunWizard from './FirstRunWizard';
import TaskAddModal from './otms/TaskAddModal';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import { isEditableKeyTarget } from '../utils/keyboardShortcuts';
import { toast } from 'react-hot-toast';

const navigation = [
  { name: 'Analyze', href: '/dashboard', icon: HomeIcon },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon },
  { name: 'People', href: '/otms/personnel', icon: UserGroupIcon },
  { name: 'Places', href: '/otms/organization-units', icon: BuildingOffice2Icon },
  { name: 'Templates', href: '/templates', icon: DocumentDuplicateIcon },
  { name: 'Rules', href: '/otms/sop', icon: DocumentTextIcon },
];

function Layout({ children }) {
  const { user, logout } = useAuth();
  const { users, customer } = useApiService();
  const location = useLocation();
  const navigate = useNavigate();
  const { getNotificationCountsByType, notifications, unreadCount } = useNotificationService();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(DEFAULT_PROFILE_IMAGE);
  const [imageError, setImageError] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [hasCheckedSetup, setHasCheckedSetup] = useState(false);
  const [showBillingBanner, setShowBillingBanner] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  // Create refs to track notification counts
  const notificationCountsRef = useRef({
    work: 0,
    engage: 0,
    talk: 0
  });
  
  // Force re-render when notification counts change
  const [notificationUpdate, setNotificationUpdate] = useState(0);
  
  // Update notification counts when notifications change
  useEffect(() => {
    if (getNotificationCountsByType) {
      const newCounts = getNotificationCountsByType();
      // Only trigger re-render if counts have changed
      if (JSON.stringify(newCounts) !== JSON.stringify(notificationCountsRef.current)) {
        notificationCountsRef.current = newCounts;
        setNotificationUpdate(prev => prev + 1);
      }
    }
  }, [notifications, unreadCount, getNotificationCountsByType]);

  useEffect(() => {
    // Load profile image if user is logged in
    const loadProfileImage = async () => {
      try {
        if (user?.id) {
          const response = await users.getProfileImageUrl(user.id);
          if (response?.url) {
            setImageUrl(response.url);
            setImageError(false);
          }
        }
      } catch (error) {
        console.error('Error loading profile image:', error);
        setImageError(true);
      }
    };
    loadProfileImage();
  }, [user?.id]);

  // Check for setup notification after first login
  useEffect(() => {
    const checkSetupNotification = async () => {
      // Only check once per session and if user is logged in
      if (hasCheckedSetup || !user?.id) return;

      try {
        // Fetch notifications to check for setup message
        const response = await api.get('/otms/notification/info');
        const notificationData = response.data;

        // Check if the response contains the exact message indicating person not found
        if (notificationData?.message === "Person not found for current user") {
          setShowSetupDialog(true);
          setHasCheckedSetup(true);
        } else {
          // Mark as checked even if no setup message found
          setHasCheckedSetup(true);
        }
      } catch (error) {
        // Check if error response contains the message
        if (error.response?.data?.message === "Person not found for current user") {
          setShowSetupDialog(true);
          setHasCheckedSetup(true);
        } else {
          console.error('Error checking setup notification:', error);
          // Don't show dialog on other errors, just mark as checked
          setHasCheckedSetup(true);
        }
      }
    };

    // Check after a short delay to ensure user is fully loaded
    const timer = setTimeout(() => {
      checkSetupNotification();
    }, 1000);

    return () => clearTimeout(timer);
  }, [user?.id, hasCheckedSetup]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        navigate('/search');
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        setShowQuickAdd(true);
        return;
      }
      if (event.key === '?' && !event.metaKey && !event.ctrlKey && !event.altKey && !isEditableKeyTarget(event.target)) {
        event.preventDefault();
        setShowShortcuts((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  // Check billing completion for Administrators (re-run on navigation and after Settings → Billing save)
  useEffect(() => {
    let cancelled = false;

    const checkBillingStatus = async () => {
      if (!user?.id || !isAdministratorRole(user?.role)) {
        if (!cancelled) setShowBillingBanner(false);
        return;
      }

      try {
        const response = await customer.getCustomerInfo();
        const customerInfo = normalizeCustomerPayload(response.data);

        const isBillingIncomplete = !isBillingInfoComplete(customerInfo);

        if (!cancelled) setShowBillingBanner(isBillingIncomplete);
      } catch (error) {
        console.error('Error checking billing status:', error);
        if (!cancelled) setShowBillingBanner(false);
      }
    };

    if (user?.id) {
      checkBillingStatus();
    }

    const onCustomerInfoUpdated = () => {
      if (user?.id) checkBillingStatus();
    };
    window.addEventListener(CUSTOMER_INFO_UPDATED_EVENT, onCustomerInfoUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener(CUSTOMER_INFO_UPDATED_EVENT, onCustomerInfoUpdated);
    };
  }, [user?.id, user?.role, customer, location.pathname]);

  return (
    <>
      {/* Billing Incomplete Banner */}
      {showBillingBanner && (
        <div className="bg-red-600 text-white px-4 py-3 text-center text-sm font-medium">
          <div className="flex items-center justify-center gap-2">
            <span>⚠️ Complete the billing info</span>
            <Link to="/settings/billing" className="underline hover:no-underline font-semibold">
              Go to Settings → Billing
            </Link>
          </div>
        </div>
      )}
      <SidebarLayout
      // Changed from bg-zinc-900 to a lighter background
      className="bg-zinc-100"
      navbar={
        <Navbar className="bg-zinc-100 border-b border-gray-200">
          <NavbarSpacer />
          <NavbarSection>
            <NavbarItem href="/search" aria-label="Search">
              <MagnifyingGlassIcon className="size-5" />
            </NavbarItem>
            <NavbarItem>
              <NotificationIcon className="bg-transparent" variant="topbar" notificationType="all" />
            </NavbarItem>
          
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                {imageError ? (
                  <InitialsAvatar name={user?.fullName || ''} className="size-8" />
                ) : (
                  <Avatar src={imageUrl} className="size-8" alt={user?.fullName} />
                )}
              </DropdownButton>
              {/* Changed from bg-zinc-800 to bg-white and hover:bg-gray-100 */}
              <DropdownMenu className="min-w-64 bg-white" anchor="bottom end">
                <DropdownItem as={Link} to="/profile" className="hover:bg-gray-100">
                  <UserIcon className="size-5" />
                  <DropdownLabel>My profile</DropdownLabel>
                </DropdownItem>
                <DropdownItem as={Link} to="/connect-ai" className="hover:bg-gray-100">
                  <SparklesIcon className="size-5" />
                  <DropdownLabel>Connect AI</DropdownLabel>
                </DropdownItem>
                <DropdownItem as={Link} to="/settings" className="hover:bg-gray-100">
                  <Cog8ToothIcon className="size-5" />
                  <DropdownLabel>Settings</DropdownLabel>
                </DropdownItem>
                {/* Changed border-zinc-700 to border-gray-200 */}
                <DropdownDivider className="border-gray-200" />
                <DropdownItem as={Link} to="/privacy-policy" className="hover:bg-gray-100">
                  <ShieldCheckIcon className="size-5" />
                  <DropdownLabel>Privacy policy</DropdownLabel>
                </DropdownItem>
                <DropdownItem as={Link} to="/share-feedback" className="hover:bg-gray-100">
                  <LightBulbIcon className="size-5" />
                  <DropdownLabel>Share feedback</DropdownLabel>
                </DropdownItem>
                <DropdownDivider className="border-gray-200" />
                <DropdownItem onClick={logout} className="hover:bg-gray-100">
                  <ArrowRightStartOnRectangleIcon className="size-5" />
                  <DropdownLabel>Sign out</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar className="bg-zinc-100 border-r border-gray-200">
          <SidebarHeader>
            <div className="flex items-center px-2 pb-3">
              <NaibLogo logoClassName="h-9 w-9" nameClassName="h-7 w-auto max-w-[130px]" alt="Onahiri" />
            </div>
            <SidebarDivider className="my-2 border-gray-200 lg:-mx-2" />
            <SidebarSection className="max-lg:hidden">
              <SidebarHeading>Workspace</SidebarHeading>
              <SidebarItem href="/work" className="hover:bg-gray-100">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <InboxIcon className="size-5" />
                    <span className="lg:hidden absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                      {getNotificationCountsByType?.()?.work > 0 ? getNotificationCountsByType().work : null}
                    </span>
                  </div>
                  <span className="flex min-w-0 flex-col">
                    <span className="text-gray-900">OWork</span>
                    <span className="text-[11px] font-normal text-gray-500">Tasks</span>
                  </span>
                  <span className="max-lg:hidden ml-auto">
                    <NotificationIcon className="bg-transparent" variant="badge" notificationType="work" />
                  </span>
                </div>
              </SidebarItem>
              
              <SidebarItem href="/engage" className="hover:bg-gray-100">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <UsersIcon className="size-5" />
                    <span className="lg:hidden absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                      {getNotificationCountsByType?.()?.engage > 0 ? getNotificationCountsByType().engage : null}
                    </span>
                  </div>
                  <span className="flex min-w-0 flex-col">
                    <span className="text-gray-900">OPulse</span>
                    <span className="text-[11px] font-normal text-gray-500">Feed</span>
                  </span>
                  <span className="max-lg:hidden ml-auto">
                    <NotificationIcon className="bg-transparent" variant="badge" notificationType="engage" />
                  </span>
                </div>
              </SidebarItem>
              
              <SidebarItem href="/chat" className="hover:bg-gray-100">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <ChatBubbleBottomCenterTextIcon className="size-5" />
                    <span className="lg:hidden absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                      {getNotificationCountsByType?.()?.talk > 0 ? getNotificationCountsByType().talk : null}
                    </span>
                  </div>
                  <span className="flex min-w-0 flex-col">
                    <span className="text-gray-900">OTalk</span>
                    <span className="text-[11px] font-normal text-gray-500">Chat</span>
                  </span>
                  <span className="max-lg:hidden ml-auto">
                    <NotificationIcon className="bg-transparent" variant="badge" notificationType="talk" />
                  </span>
                </div>
              </SidebarItem>
            </SidebarSection>
          </SidebarHeader>

          <SidebarBody>
            <SidebarSection>
              <SidebarHeading>Organize</SidebarHeading>
              {navigation.map((item) => (
                <SidebarItem 
                  key={item.name}
                  as={Link}
                  to={item.href}
                  className="hover:bg-gray-100"
                >
                  <item.icon className="size-5" />
                  <SidebarLabel>{item.name}</SidebarLabel>
                </SidebarItem>
              ))}
            </SidebarSection>

            <SidebarSpacer />

            <SidebarSection>
              <SidebarItem
                as={Link}
                to="https://onahiri.com/docs/onahiri/"
                className="hover:bg-gray-100"
                target="_blank"
                rel="noopener noreferrer"
              >
                <QuestionMarkCircleIcon className="size-5" />
                <SidebarLabel>Help</SidebarLabel>
              </SidebarItem>
              <SidebarItem as={Link} to="/settings" className="hover:bg-gray-100">
                <Cog6ToothIcon className="size-5" />
                <SidebarLabel>Settings</SidebarLabel>
              </SidebarItem>
              <SidebarItem className="hover:bg-gray-100">
                <NotificationIcon className="bg-transparent" variant="sidebar" label="Notifications" notificationType="all" />
              </SidebarItem>
            </SidebarSection>
          </SidebarBody>

          <SidebarFooter className="max-lg:hidden">
            <Dropdown>
              <DropdownButton as={SidebarItem} className="hover:bg-gray-100">
                <span className="flex min-w-0 items-center gap-3">
                  {imageError ? (
                    <InitialsAvatar name={user?.fullName || ''} className="size-10" />
                  ) : (
                    <Avatar src={imageUrl} className="size-10" alt={user?.fullName} />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate text-sm/5 font-medium">
                      {user?.fullName}
                    </span>
                    <span className="block truncate text-xs/5 font-normal">
                      {user?.email}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon className="size-5" />
              </DropdownButton>
              {/* Changed from bg-zinc-800 to bg-white, hover classes also updated */}
              <DropdownMenu className="min-w-64 bg-white" anchor="top start">
                <DropdownItem as={Link} to="/profile" className="hover:bg-gray-100">
                  <UserIcon className="size-5" />
                  <DropdownLabel>My profile</DropdownLabel>
                </DropdownItem>
                <DropdownItem as={Link} to="/connect-ai" className="hover:bg-gray-100">
                  <SparklesIcon className="size-5" />
                  <DropdownLabel>Connect AI</DropdownLabel>
                </DropdownItem>
                <DropdownItem as={Link} to="/settings" className="hover:bg-gray-100">
                  <Cog8ToothIcon className="size-5" />
                  <DropdownLabel>Settings</DropdownLabel>
                </DropdownItem>
                <DropdownDivider className="border-gray-200" />
             
              
                <DropdownDivider className="border-gray-200" />
                <DropdownItem onClick={logout} className="hover:bg-gray-100">
                  <ArrowRightStartOnRectangleIcon className="size-5" />
                  <DropdownLabel>Sign out</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
    >
      <div className="bg-white m-0 p-0">
        {children}
      </div>

      <FirstRunWizard open={showSetupDialog} onClose={() => setShowSetupDialog(false)} />
      <TaskAddModal
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onTaskCreated={() => {
          toast.success('Task created');
          if (!location.pathname.startsWith('/work')) {
            navigate('/work');
          }
        }}
      />
      <KeyboardShortcutsHelp open={showShortcuts} onClose={() => setShowShortcuts(false)} />
      </SidebarLayout>
    </>
  );
}

export default Layout;
